import type { CommentStatus, Replier } from '@/services/comments/types';

export interface CommentUserInfo {
  userName: string;
  email: string | null;
}

export interface Position {
  selector?: string | null;
  offsetXPct?: number | null;
  offsetYPct?: number | null;
  innerPath?: string | null;
}

export interface FeedbackCommentLocation {
  x?: number;
  y?: number;
  position?: Position | null;
  breakpoint?: { id?: string; minWidth?: number; maxWidth?: number } | null;
  innerPath?: string | null;
  pageId?: string | null;
}

/** Addressed by its index in `replies`; persisted replies have no id. */
export interface CommentReply {
  replier?: Replier | null;
  message?: string;
  createdDate?: Date | null;
  /** Client-only reply kept in local state until comments are reloaded. */
  isLocalOnly?: boolean;
  /** Client-only key of an optimistic reply, to drop it if the write fails. */
  localId?: number;
}

export interface FeedbackComment {
  id?: string | null;
  feedbackId?: string | null;
  commenterId?: string | null;
  message?: string | null;
  createdDate?: string | Date | null;
  status?: CommentStatus | null;
  revision?: string | null;
  commentLocation?: FeedbackCommentLocation | null;
  replies?: CommentReply[];
}

export interface CommentsData {
  comments: FeedbackComment[];
  commenterInfoMap: Record<string, CommentUserInfo>;
  /** The agency that owns the site. Set on the owner page only. */
  agencyName?: string;
}

export interface SessionData {
  visitorId: string;
  userInfo: CommentUserInfo;
}

export interface InitializeData {
  comments: FeedbackComment[];
  userInfo?: CommentUserInfo;
  visitorId?: string;
  commenterInfoMap?: Record<string, CommentUserInfo>;
  agencyName?: string;
  siteId?: string;
}

export interface Pin {
  id: string;
  selector: string;
  offsetXPct: number;
  offsetYPct: number;
  fallbackXPct: number;
  fallbackYPct: number;
  label: string;
  createdAt: number;
  commentId?: string;
  commenterId?: string | null;
  status?: CommentStatus | null;
  revision?: string | null;
  devicePreset?: DevicePreset | null;
  pagePath?: string;
  replies?: CommentReply[];
  isLegacy?: boolean;
  legacyX?: number;
  legacyY?: number;
}

export interface BridgePinData {
  id: string;
  selector: string;
  offsetXPct: number;
  offsetYPct: number;
  fallbackXPct: number;
  fallbackYPct: number;
  /** Initial shown in the avatar-style pin marker. */
  label: string;
  avatarColor?: string;
  status?: CommentStatus | null;
  isLegacy?: boolean;
  legacyX?: number;
  legacyY?: number;
}

export interface BridgePinClickMessage {
  type: 'FB_PIN_CLICK';
  payload: {
    selector: string;
    offsetXPct: number;
    offsetYPct: number;
    clientX: number;
    clientY: number;
    pageWidth: number;
    pageHeight?: number;
    scrollX?: number;
    scrollY?: number;
    pathname: string;
  };
}

export interface BridgeSetModeMessage {
  type: 'FB_SET_MODE';
  payload: { pinMode: boolean; inspectOnly?: boolean };
}

export interface BridgeSetPinsMessage {
  type: 'FB_SET_PINS';
  payload: { pins: BridgePinData[] };
}

export interface BridgeSetPinsVisibleMessage {
  type: 'FB_SET_PINS_VISIBLE';
  payload: { visible: boolean };
}

export interface BridgeScrollToPinMessage {
  type: 'FB_SCROLL_TO_PIN';
  payload: { pinId: string };
}

export interface BridgePinMarkerClickMessage {
  type: 'FB_PIN_MARKER_CLICK';
  payload: {
    pinId: string;
    /** Center of the pin badge in iframe viewport coordinates */
    x: number;
    y: number;
  };
}

export interface BridgePinMovedMessage {
  type: 'FB_PIN_MOVED';
  payload: {
    pinId: string;
    /** Selector of the element under the drop point (re-anchors legacy pins). */
    selector: string;
    offsetXPct: number;
    offsetYPct: number;
    fallbackXPct: number;
    fallbackYPct: number;
  };
}

export interface BridgeScrolledToPinMessage {
  type: 'FB_SCROLLED_TO_PIN';
  payload: {
    pinId: string;
    /** Center of the pin badge in iframe viewport coordinates after scroll */
    x: number;
    y: number;
  };
}

export interface BridgeSetScrollLockedMessage {
  type: 'FB_SET_SCROLL_LOCKED';
  payload: { locked: boolean };
}

export interface BridgeSetSelectionDisabledMessage {
  type: 'FB_SET_SELECTION_DISABLED';
  payload: { disabled: boolean };
}

export interface BridgeNavigateMessage {
  type: 'FB_NAVIGATE';
  payload: { url: string };
}

export interface BridgeSyncScrollMessage {
  type: 'FB_SYNC_SCROLL';
  payload: { scrollY: number };
}

export interface BridgeClearHighlightMessage {
  type: 'FB_CLEAR_HIGHLIGHT';
}

export type BridgeOutboundMessage =
  | BridgeSetModeMessage
  | BridgeSetPinsMessage
  | BridgeSetPinsVisibleMessage
  | BridgeScrollToPinMessage
  | BridgeSetScrollLockedMessage
  | BridgeSetSelectionDisabledMessage
  | BridgeNavigateMessage
  | BridgeSyncScrollMessage
  | BridgeClearHighlightMessage;

export interface BridgePageChangedMessage {
  type: 'FB_PAGE_CHANGED';
  payload: { pathname: string };
}

export interface BridgeScrollMessage {
  type: 'FB_SCROLL';
  payload: { scrollY: number };
}

export interface BridgeDismissPopoverMessage {
  type: 'FB_DISMISS_POPOVER';
}

export type BridgeInboundMessage =
  | BridgePinClickMessage
  | BridgePinMarkerClickMessage
  | BridgePinMovedMessage
  | BridgeScrolledToPinMessage
  | BridgePageChangedMessage
  | BridgeScrollMessage
  | BridgeDismissPopoverMessage;

export enum ProxyInitError {
  AccountMismatch = 'account_mismatch',
  NotFound = 'not_found',
  ErrorGeneric = 'errorGeneric',
}

export interface ProxyInitPayload {
  user?: import('@/hooks/useCurrentUser').CurrentUser | null;
  initialData?: InitializeData | null;
  error?: ProxyInitError;
  // /partner has no identity to resolve its own locale, so /partner/proxy's
  // already-resolved locale/messages ride along here.
  locale?: string;
  messages?: import('next-intl').AbstractIntlMessages;
}

export interface ProxyResolveCommentMessage {
  type: 'FB_RESOLVE_COMMENT';
  payload: {
    pinId: string;
    commentId: string;
    revision?: string | null;
    feedbackId: string;
    metaSiteId: string;
  };
}

export interface ProxyCommentResolvedMessage {
  type: 'FB_COMMENT_RESOLVED';
  payload: {
    pinId: string;
    revision: string | null;
  };
}

export interface ProxyReopenCommentMessage {
  type: 'FB_REOPEN_COMMENT';
  payload: {
    pinId: string;
    commentId: string;
    revision?: string | null;
  };
}

export interface ProxyCommentReopenedMessage {
  type: 'FB_COMMENT_REOPENED';
  payload: {
    pinId: string;
    revision: string | null;
  };
}

export interface ProxyReopenCommentFailedMessage {
  type: 'FB_REOPEN_COMMENT_FAILED';
  payload: { pinId: string };
}

export interface ProxyResolveCommentFailedMessage {
  type: 'FB_RESOLVE_COMMENT_FAILED';
  payload: { pinId: string };
}

export interface ProxyMarkCommentReadMessage {
  type: 'FB_MARK_COMMENT_READ';
  payload: {
    pinId: string;
    commentId: string;
    revision?: string | null;
    feedbackId: string;
    metaSiteId: string;
  };
}

export interface ProxyCommentMarkedReadMessage {
  type: 'FB_COMMENT_MARKED_READ';
  payload: {
    pinId: string;
    revision: string | null;
    status?: CommentStatus | null;
  };
}

export interface ProxyMarkCommentReadFailedMessage {
  type: 'FB_MARK_COMMENT_READ_FAILED';
  payload: { pinId: string };
}

export interface ProxyReplyToCommentMessage {
  type: 'FB_REPLY_TO_COMMENT';
  payload: {
    pinId: string;
    optimisticReplyId: number;
    commentId: string;
    message: string;
    revision?: string | null;
  };
}

export interface ProxyCommentRepliedMessage {
  type: 'FB_COMMENT_REPLIED';
  payload: {
    pinId: string;
    replies: CommentReply[];
    revision: string | null;
  };
}

export interface ProxyReplyToCommentFailedMessage {
  type: 'FB_REPLY_TO_COMMENT_FAILED';
  payload: { pinId: string; optimisticReplyId: number };
}

export interface ProxyDeleteReplyMessage {
  type: 'FB_DELETE_REPLY';
  payload: {
    pinId: string;
    commentId: string;
    replyIndex: number;
    revision?: string | null;
  };
}

export interface ProxyReplyDeletedMessage {
  type: 'FB_REPLY_DELETED';
  payload: {
    pinId: string;
    replies: CommentReply[];
    revision: string | null;
  };
}

export interface ProxyDeleteReplyFailedMessage {
  type: 'FB_DELETE_REPLY_FAILED';
  payload: { pinId: string; replyIndex: number };
}

export interface ProxyGetShareLinkMessage {
  type: 'FB_GET_SHARE_LINK';
  payload: { metaSiteId: string };
}

export interface ProxyShareLinkUrlMessage {
  type: 'FB_SHARE_LINK_URL';
  payload: { shareUrl: string };
}

export interface ProxyShareLinkFailedMessage {
  type: 'FB_SHARE_LINK_FAILED';
}

export enum EditorType {
  Studio_Two = 'STUDIO_TWO',
  Studio = 'STUDIO',
  Editor = 'EDITOR',
  EditorX = 'EDITORX',
  Odeditor = 'ODEDITOR',
  Unknown = 'UNKNOWN',
}

export interface LinkInfo {
  metaSiteId?: string;
  feedbackId?: string;
  editorType?: EditorType;
  siteId?: string;
  siteName?: string;
  ownerName?: string;
}

export type DevicePreset = 'mobile' | 'desktop';

export const DEVICE_WIDTHS: Record<DevicePreset, number> = {
  mobile: 390,
  desktop: 1280,
};
